/**
 * File: chunkReload.ts
 * Description: Recovers from stale dynamically-imported chunks after a new
 *   deployment. Vite content-hashes lazily-loaded chunks (including Mermaid's
 *   per-diagram renderers such as flowDiagram-*.js and c4Diagram-*.js). When a
 *   new build is deployed, an already-open browser tab still references the
 *   previous hashes, so importing them 404s with "Failed to fetch dynamically
 *   imported module". Reloading the page fetches the fresh index + chunks.
 *   A short cooldown prevents reload loops.
 */

/** Minimum time between automatic reloads, in milliseconds. */
const RELOAD_COOLDOWN_MS = 10000;

/** sessionStorage key holding the timestamp of the last automatic reload. */
const RELOAD_TIMESTAMP_KEY = 'archie:last-chunk-reload';

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /'?text\/html'? is not a valid javascript mime type/i,
];

/**
 * Whether an error message indicates a failed dynamic chunk import (a stale
 * deployment), as opposed to an application/diagram error.
 *
 * @param message - The error message to inspect.
 * @returns True when the message matches a known chunk-load failure.
 */
export const isChunkLoadError = (message: string): boolean =>
  CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));

/**
 * Reload the page at most once per cooldown window to recover from a stale
 * deployment without entering a reload loop.
 */
const reloadOnce = (): void => {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? '0');
  } catch {
    last = 0;
  }

  if (Date.now() - last < RELOAD_COOLDOWN_MS) {
    return;
  }

  try {
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures (e.g. private mode); still attempt the reload.
  }

  globalThis.location.reload();
};

/**
 * Register global listeners that reload the page when a dynamically imported
 * chunk fails to load after a new deployment.
 */
export const registerChunkReloadHandler = (): void => {
  // Vite dispatches this when its preload helper fails to import a chunk.
  globalThis.addEventListener('vite:preloadError', (event: Event) => {
    event.preventDefault();
    reloadOnce();
  });

  // Fallback for chunk-load rejections not routed through Vite's helper.
  globalThis.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? reason.message : String(reason ?? '');
    if (isChunkLoadError(message)) {
      reloadOnce();
    }
  });
};
